import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import * as _ from 'lodash-es';
import { environment } from '../../../../../environments/environment';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { EditComponentDialogComponent } from '../../../../core/executive-dashboards/components/edit-component-dialog/edit-component-dialog.component';
import { GlobalService } from '../../../../global/services/global.service';
import { AdminDashboardsService } from '../admin-dashboards.service';

import { safeJsonInput } from '../../../../shared/_utils/safe-json-input';
interface SheetRow {
  sheet_key?: any;
  name: string;
  action_code: string;
  action_id: string;
}

interface SheetOption {
  name: string;
  value: string;
  action: string;
}

interface FilterMultiSelect {
  values: SheetOption[];
  dropdownValues: SheetOption[];
  name: string;
  predicate: any[];
  selected: SheetOption[];
  predicates: string[];
  disablePredicates?: boolean;
  loadingPredicate?: boolean;
}

interface FilterRow {
  name: string;
  predicate: string;
  filter_key: string;
  type: string;
  operator: string;
  reverse: boolean;
  sheet: string[];
  filter_order?: number;
  sheetMultiSelectObj: FilterMultiSelect;
}

interface ChartBox {
  columns?: number | string;
  component_id?: any;
  component_type?: string;
  title?: string;
  comp_name?: string;
  strategy?: string;
  height?: string;
  filterable?: boolean;
  srcSheet?: string;
  rowIndex?: number;
  row_title?: string;
  extraOptions?: any;
  hide?: boolean;
  merged?: boolean;
  deleting?: boolean;
}

interface DashboardModel {
  dashboard_id?: any;
  dashboard_key?: any;
  dashboard_name?: string;
  dashboard_type?: string;
  dashboard_desc?: string;
  dashboard_alert_text?: string;
  data_sheets: SheetRow[];
  filters: FilterRow[];
  components?: ChartBox[][];
}

const COMBINATION_CHART_TYPE = 'combinationChart';

const EMPTY_FILTER: Omit<FilterRow, 'sheetMultiSelectObj'> = {
  name: '',
  predicate: '',
  filter_key: '',
  type: 'array',
  operator: '[]',
  reverse: false,
  sheet: [],
};

/**
 * Add/Edit Admin Dashboard modal.
 *
 * Migrated from `adminDashComponentCtrl` in
 * `GTW12/ui/app/components/admin/adminDashboards/adminDashboardCtrl.js`
 * (template `admin-dashboard-component.html`).
 *
 * Reference for migration patterns: `AdminActionEditComponent`
 * (see ADMIN_TAB_MIGRATION_GUIDE.md, sections 6.x).
 *
 * Chart component editor reuses the already-migrated
 * `EditComponentDialogComponent` from `core/executive-dashboards`.
 *
 * Note on Merge/Unmerge: the original `mergeChartComponentCtrl` is not yet
 * migrated, so the inline merge/unmerge buttons are hidden in this iteration.
 */
@Component({
    selector: 'gtw-admin-dashboard-edit',
    templateUrl: './admin-dashboard-edit.component.html',
    styleUrls: ['./admin-dashboard-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminDashboardEditComponent implements OnInit, OnDestroy {
  loading = false;
  crudLoading = false;
  minimise = false;
  submitted = false;
  title = 'Admin Dashboard Component';
  mode: 'new' | 'edit' = 'new';

  dashboard: DashboardModel = { data_sheets: [], filters: [] };

  /** Cache of action_code → first row of jsonObject, used to compute predicates. */
  actionDataCache: Record<string, any> = {};
  /** Last full-state copy of filters to compute removed filters on save. */
  clonedDashboardFilters: FilterRow[] = [];

  /** Native HTML5 drag/drop state for chart-row reordering (Pitfall M10). */
  rowDragIndex: number | null = null;
  /** Native HTML5 drag/drop for box reordering inside a row. */
  boxDrag: { rowIdx: number; idx: number } | null = null;

  private rowData: any = {};
  private destroy$ = new Subject<void>();
  private adminApiUrl = environment.admin_api_url;
  private baseUrl = environment.base_url;

  constructor(
    @Optional() private dialogRef: MatDialogRef<AdminDashboardEditComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: any,
    private service: AdminDashboardsService,
    private dialog: MatDialog,
    private snackbar: GtwSnackbarService,
    private globalService: GlobalService,
    private cdr: ChangeDetectorRef,
  ) {
    this.rowData = data?.rowData || {};
    this.mode =
      data?.mode ||
      (this.rowData?.DASHBOARD_KEY || this.rowData?.DASHBOARD_ID ? 'edit' : 'new');
  }

  ngOnInit(): void {
    this.title = (this.mode === 'edit' ? 'Edit ' : 'New ') + 'Admin Dashboard Component';
    if (this.mode === 'edit' && this.rowData?.DASHBOARD_ID != null) {
      this.loadDashboardByKey(this.rowData.DASHBOARD_ID);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  private loadDashboardByKey(dashboardId: any): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.service.getDashboardByKey(this.baseUrl, dashboardId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (res?.dashboard) {
            this.dashboard = this.normalizeDashboard(res.dashboard);
            this.clonedDashboardFilters = _.cloneDeep(this.dashboard.filters);
            this.dashboard.filters.forEach((f) => this.sheetChange(f, false));
            this.setupCombinationCharts();
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  /** Build allSheets and rebuild every filter's sheetMultiSelectObj. */
  private normalizeDashboard(raw: any): DashboardModel {
    const dashboard: DashboardModel = {
      dashboard_id: raw.dashboard_id,
      dashboard_key: raw.dashboard_key,
      dashboard_name: raw.dashboard_name,
      dashboard_type: raw.dashboard_type,
      dashboard_desc: raw.dashboard_desc,
      dashboard_alert_text: raw.dashboard_alert_text,
      data_sheets: Array.isArray(raw.data_sheets) ? raw.data_sheets : [],
      filters: [],
      components: Array.isArray(raw.components) ? raw.components : [],
    };

    const allSheets = this.buildAllSheets(dashboard.data_sheets);

    if (Array.isArray(raw.filters)) {
      dashboard.filters = raw.filters.map((f: any) => {
        const selected = allSheets.filter((opt) =>
          Array.isArray(f.sheet) ? f.sheet.includes(opt.value) : false,
        );
        const filterRow: FilterRow = {
          name: f.name || '',
          predicate: f.predicate || '',
          filter_key: f.filter_key || '',
          type: f.type || 'array',
          operator: f.operator || '[]',
          reverse: !!f.reverse,
          sheet: Array.isArray(f.sheet) ? [...f.sheet] : [],
          filter_order: f.filter_order,
          sheetMultiSelectObj: {
            values: allSheets,
            dropdownValues: this.toAppkitSheetOptions(allSheets),
            name: 'Sheets',
            predicate: [],
            selected,
            predicates: [],
            disablePredicates: false,
          },
        };
        return filterRow;
      });
    }

    return dashboard;
  }

  private buildAllSheets(sheets: SheetRow[]): SheetOption[] {
    return (sheets || []).map((s) => ({
      name: s.name,
      value: s.name,
      action: s.sheet_key
        ? this.baseUrl + '/' + s.action_id
        : this.baseUrl + '/loadJsonObject?action_code=' + s.action_id,
    }));
  }

  private setupCombinationCharts(): void {
    if (!this.dashboard.components) return;
    this.dashboard.components.forEach((row) => {
      const first = row[0];
      if (first && first.component_type === COMBINATION_CHART_TYPE) {
        try {
          const extra =
            typeof first.extraOptions === 'string'
              ? safeJsonInput<any>(first.extraOptions, null, 'admin-dashboard-edit.parse1')
              : first.extraOptions;
          first.hide = true;
          if (extra?.componentSettings?.parentChartOptions) {
            extra.componentSettings.parentChartOptions.merged = true;
            row.push(extra.componentSettings.parentChartOptions);
          }
          if (extra?.componentSettings?.childChartOptions) {
            extra.componentSettings.childChartOptions.merged = true;
            row.push(extra.componentSettings.childChartOptions);
          }
        } catch {
          /* swallow — original controller does the same */
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Sheet rows
  // ---------------------------------------------------------------------------

  addSheetRow(): void {
    if (!this.dashboard.data_sheets) {
      this.dashboard.data_sheets = [];
    }
    this.dashboard.data_sheets.push({ name: '', sheet_key: '', action_id: '', action_code: '' });
    this.refreshFilterOptionsFromSheets();
  }

  removeSheetRow(index: number): void {
    const sheet = this.dashboard.data_sheets[index];
    if (sheet && sheet.sheet_key) {
      this.service.deleteSheet(this.baseUrl, { sheet_key: sheet.sheet_key, sheet_name: sheet.name })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            if (res?.callSuccess === '1') {
              this.dashboard.data_sheets.splice(index, 1);
              this.refreshFilterOptionsFromSheets();
              this.snackbar.openSnackBar('Sheet successfully deleted', 'gtw-snackbar--green');
              this.cdr.detectChanges();
            } else {
              this.snackbar.openSnackBar(res?.errorMessage || 'Failed to delete sheet', 'gtw-snackbar--red');
            }
          },
          error: () => {
            this.snackbar.openSnackBar('Failed to delete sheet', 'gtw-snackbar--red');
          },
        });
    } else {
      this.dashboard.data_sheets.splice(index, 1);
      this.refreshFilterOptionsFromSheets();
    }
  }

  /**
   * Mirrors the original `selectSheetObj()` — rebuilds allSheets from the
   * current data_sheets and updates every filter's available values.
   */
  onSheetNameChange(): void {
    this.refreshFilterOptionsFromSheets();
  }

  private refreshFilterOptionsFromSheets(): void {
    const allSheets = this.buildAllSheets(this.dashboard.data_sheets);
    (this.dashboard.filters || []).forEach((f) => {
      f.sheetMultiSelectObj.values = allSheets;
      f.sheetMultiSelectObj.dropdownValues = this.toAppkitSheetOptions(allSheets);
      f.sheetMultiSelectObj.selected = allSheets.filter((opt) => f.sheet.includes(opt.value));
    });
  }

  // ---------------------------------------------------------------------------
  // Filter rows
  // ---------------------------------------------------------------------------

  addFilterRow(): void {
    if (!this.dashboard.filters) {
      this.dashboard.filters = [];
    }
    const allSheets = this.buildAllSheets(this.dashboard.data_sheets);
    const newFilter: FilterRow = {
      ...EMPTY_FILTER,
      sheet: [],
      sheetMultiSelectObj: {
        values: allSheets,
        dropdownValues: this.toAppkitSheetOptions(allSheets),
        name: 'Sheets',
        predicate: [],
        selected: [],
        predicates: [],
        disablePredicates: false,
      },
    };
    this.dashboard.filters.push(newFilter);
  }

  removeFilterRow(index: number): void {
    const filter = this.dashboard.filters[index];
    if (filter && filter.filter_key) {
      this.service.deleteFilterByKey(this.baseUrl, {
        filter_name: filter.name,
        dashboard_id: this.dashboard.dashboard_id,
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            if (res?.callSuccess === '1') {
              this.dashboard.filters.splice(index, 1);
              this.clonedDashboardFilters = _.cloneDeep(this.dashboard.filters);
              this.snackbar.openSnackBar('Filter successfully deleted', 'gtw-snackbar--green');
              this.cdr.detectChanges();
            } else {
              this.snackbar.openSnackBar(res?.errorMessage || 'Failed to delete filter', 'gtw-snackbar--red');
            }
          },
          error: () => {
            this.snackbar.openSnackBar('Failed to delete filter', 'gtw-snackbar--red');
          },
        });
    } else {
      this.dashboard.filters.splice(index, 1);
      this.clonedDashboardFilters = _.cloneDeep(this.dashboard.filters);
    }
  }

  /** Keep selected option objects and persisted sheet names in sync. */
  onFilterSheetSelectionChange(filter: FilterRow, selected: SheetOption[]): void {
    filter.sheetMultiSelectObj.selected = selected || [];
    filter.sheet = filter.sheetMultiSelectObj.selected.map((option) => option.value);
    this.sheetChange(filter, true);
  }

  private toAppkitSheetOptions(items: SheetOption[]): SheetOption[] {
    return (items || []).map((item) => ({
      ...item,
      showCheckbox: true,
      role: 'checkbox',
    }) as SheetOption);
  }

  /**
   * Mirrors the original `vm.sheetChange(filter)` — loads action data for any
   * newly selected sheet and recomputes the predicate intersection.
   */
  private sheetChange(filter: FilterRow, fireDetectChanges = true): void {
    const selectedActions = filter.sheetMultiSelectObj.selected.map((s) => s.action);
    const cachedActions = Object.keys(this.actionDataCache);
    const toLoad = _.difference(selectedActions, cachedActions);
    const toRemove = _.difference(cachedActions, selectedActions);

    filter.sheetMultiSelectObj.predicate = [];

    if (toLoad.length > 0) {
      filter.sheetMultiSelectObj.loadingPredicate = true;
      if (fireDetectChanges) this.cdr.detectChanges();

      let pending = toLoad.length;
      const globalParams = this.globalService?.global?.globalParams || {};
      toLoad.forEach((actionUrl) => {
        // Mirror legacy `_.merge({}, GlobalService.globalParams, {is_admin:'Y'})`.
        // Without these context params (tax_year/scenario/jcd_key/...) some
        // action handlers return only stub rows.
        this.service.getActionData(actionUrl, { ...globalParams, is_admin: 'Y' })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (data) => {
              if (data) {
                const firstRow = Array.isArray(data) ? data[0] : data?.jsonObject?.[0];
                if (firstRow) {
                  this.actionDataCache[actionUrl] = firstRow;
                }
              }
              if (--pending <= 0) {
                filter.sheetMultiSelectObj.loadingPredicate = false;
                this.populatePredicate(filter, selectedActions);
                if (fireDetectChanges) this.cdr.detectChanges();
              }
            },
            error: () => {
              if (--pending <= 0) {
                filter.sheetMultiSelectObj.loadingPredicate = false;
                this.populatePredicate(filter, selectedActions);
                if (fireDetectChanges) this.cdr.detectChanges();
              }
            },
          });
      });
    } else if (toRemove.length > 0) {
      // selectedActions removed → predicates already recomputed by intersection below
      this.populatePredicate(filter, selectedActions);
      if (fireDetectChanges) this.cdr.detectChanges();
    } else {
      this.populatePredicate(filter, selectedActions);
      if (fireDetectChanges) this.cdr.detectChanges();
    }
  }

  private populatePredicate(filter: FilterRow, selectedActions: string[]): void {
    const notLoaded = _.difference(selectedActions, Object.keys(this.actionDataCache));
    if (notLoaded.length > 0) return;

    filter.sheetMultiSelectObj.disablePredicates = false;

    if (selectedActions.length === 1) {
      const cached = this.actionDataCache[selectedActions[0]];
      filter.sheetMultiSelectObj.predicates = cached ? Object.keys(cached) : [];
    } else if (selectedActions.length > 1) {
      const collections = selectedActions.map((a) => Object.keys(this.actionDataCache[a] || {}));
      filter.sheetMultiSelectObj.predicates = _.intersection(...collections);
      if (filter.sheetMultiSelectObj.predicates.length === 0) {
        filter.sheetMultiSelectObj.predicates = ['No Common Predicates found between sheets'];
        filter.sheetMultiSelectObj.disablePredicates = true;
        filter.predicate = 'No Common Predicates found between sheets';
      }
    } else {
      filter.sheetMultiSelectObj.predicates = [];
    }
  }

  // ---------------------------------------------------------------------------
  // Components List (chart boxes)
  // ---------------------------------------------------------------------------

  addChartRow(): void {
    if (!this.dashboard.components) {
      this.dashboard.components = [];
    }
    this.dashboard.components.push([{ columns: 12 }]);
  }

  addBox(row: ChartBox[]): void {
    if (row.length >= 4) return;
    if (row[0]?.component_type === COMBINATION_CHART_TYPE) return;
    row.push({});
    this.adjustColMd(row);
  }

  /** Open the chart editor for a new or existing box. */
  openChartEditor(rowIdx: number, boxIdx: number, isCreateNew: boolean): void {
    if (!this.dashboard.dashboard_id) {
      this.snackbar.openSnackBar('Save the dashboard first to add components.', 'gtw-snackbar--orange');
      return;
    }

    const row = this.dashboard.components![rowIdx];
    const box = row[boxIdx];
    const editObj = isCreateNew ? {} : this.prepareEditObj(box);

    // Diagnostic — verify the API actually returned the JS-friendly fields
    // (`component_type`, `strategy`, `srcSheet`, parsed `extraOptions`) we
    // expect to merge into EditComponentDialogComponent's `component`.
    console.log('[AdminDashboardEdit] Opening chart editor with box:', box);
    console.log('[AdminDashboardEdit] Sheets passed to editor:', this.dashboard.data_sheets);

    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = ['admin-modal-panel', 'admin-child-modal-panel'];
    dialogConfig.width = '90vw';
    dialogConfig.maxWidth = '1200px';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      editObj,
      sheetsObj: this.dashboard.data_sheets || [],
      fullData: false,
      dashboard_id: this.dashboard.dashboard_id,
      rowIndex: String(rowIdx + 1),
      columns: String(box.columns ?? 12),
    };

    const ref = this.dialog.open(EditComponentDialogComponent, dialogConfig);
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((savedComponent: any) => {
        if (savedComponent) {
          this.save(true, true);
        }
      });
  }

  private prepareEditObj(box: ChartBox): any {
    const cloned: any = _.cloneDeep(box);
    if (typeof cloned.extraOptions === 'string') {
      try {
        cloned.extraOptions = safeJsonInput<any>(cloned.extraOptions, null, 'admin-dashboard-edit.parse2');
      } catch {
        cloned.extraOptions = {};
      }
    }
    return cloned;
  }

  deleteBox(rowIdx: number, boxIdx: number): void {
    const row = this.dashboard.components![rowIdx];
    const box = row[boxIdx];
    if (box?.component_id == null) {
      row.splice(boxIdx, 1);
      this.adjustColMd(row);
      this.cleanEmptyRows();
      return;
    }

    box.deleting = true;
    this.cdr.detectChanges();

    this.service.deleteComponentByKey(this.baseUrl, { comp_key: box.component_id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          box.deleting = false;
          if (res?.callSuccess === '1') {
            row.splice(boxIdx, 1);
            this.adjustColMd(row);
            this.cleanEmptyRows();
            this.save(true, true);
            this.snackbar.openSnackBar('Component successfully deleted', 'gtw-snackbar--green');
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Failed to delete component', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          box.deleting = false;
          this.snackbar.openSnackBar('Failed to delete component', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  /** Original `adjustColMd` — keep total columns at 12 across visible boxes in a row. */
  private adjustColMd(row: ChartBox[]): void {
    if (!this.dashboard.components) return;
    if (row[0]?.component_type === COMBINATION_CHART_TYPE) return;
    if (row.length === 0) return;
    const cols = Math.floor(12 / row.length);
    row.forEach((b) => (b.columns = cols));
  }

  private cleanEmptyRows(): void {
    if (!this.dashboard.components) return;
    this.dashboard.components = this.dashboard.components.filter((r) => r && r.length > 0);
  }

  /** True for the last visible (non-hidden) box in the row. */
  isLastVisibleBox(row: ChartBox[], boxIdx: number): boolean {
    for (let i = row.length - 1; i >= 0; i--) {
      if (!row[i].hide) {
        return i === boxIdx;
      }
    }
    return false;
  }

  /** Tracks rows by reference so HTML5 drag/drop reorders cleanly. */
  trackRow = (_index: number, row: SheetRow | FilterRow | ChartBox[]): SheetRow | FilterRow | ChartBox[] => row;

  // ---- Native HTML5 D&D for chart-row reordering (Pitfall M10) ---------------

  onRowDragStart(event: DragEvent, idx: number): void {
    this.rowDragIndex = idx;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onRowDragEnter(idx: number): void {
    if (this.rowDragIndex == null || this.rowDragIndex === idx) return;
    if (!this.dashboard.components) return;
    const [moved] = this.dashboard.components.splice(this.rowDragIndex, 1);
    this.dashboard.components.splice(idx, 0, moved);
    this.rowDragIndex = idx;
  }

  onRowDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onRowDragEnd(): void {
    this.rowDragIndex = null;
  }

  // ---- Native HTML5 D&D for boxes within a row ------------------------------

  onBoxDragStart(event: DragEvent, rowIdx: number, idx: number): void {
    event.stopPropagation();
    this.boxDrag = { rowIdx, idx };
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onBoxDragEnter(rowIdx: number, idx: number): void {
    if (!this.boxDrag) return;
    if (this.boxDrag.rowIdx !== rowIdx) return;
    if (this.boxDrag.idx === idx) return;
    const row = this.dashboard.components![rowIdx];
    if (row[0]?.component_type === COMBINATION_CHART_TYPE) return;
    const [moved] = row.splice(this.boxDrag.idx, 1);
    row.splice(idx, 0, moved);
    this.boxDrag.idx = idx;
  }

  onBoxDragEnd(): void {
    this.boxDrag = null;
  }

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  save(silent = false, skipValidation = false): void {
    if (!skipValidation) {
      this.submitted = true;
      this.cdr.detectChanges();
      if (!this.isFormValid()) {
        return;
      }
    }

    const filterArray = this.buildFilterArrayForSave();
    const components = this.buildComponentDataForSave();

    const params: Record<string, any> = {
      data_sheets: JSON.stringify(this.dashboard.data_sheets || []),
      filters: JSON.stringify(filterArray),
      dashboard_id: this.dashboard.dashboard_id,
      dashboard_name: this.dashboard.dashboard_name,
      dashboard_key: this.dashboard.dashboard_key,
      dashboard_type: this.dashboard.dashboard_type,
      dashboard_desc: this.dashboard.dashboard_desc,
      dashboard_alert_text: this.dashboard.dashboard_alert_text,
    };

    if (components.length > 0) {
      params.comp_data = JSON.stringify(components);
    }

    this.crudLoading = true;
    this.cdr.detectChanges();

    this.service.createDashboard(this.baseUrl, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.crudLoading = false;
          if (res?.callSuccess === '1') {
            const newId = res.dashboard_id ?? this.dashboard.dashboard_id;
            const wasNew = !this.dashboard.dashboard_id;
            if (newId) {
              this.dashboard.dashboard_id = newId;
              // Reload to pick up server-assigned keys (sheet_key, filter_key, dashboard_key)
              this.loadDashboardByKey(newId);
            }
            if (!silent) {
              this.snackbar.openSnackBar(
                wasNew ? 'Dashboard created successfully' : 'Dashboard updated successfully',
                'gtw-snackbar--green',
              );
              if (this.dialogRef) {
                this.dialogRef.close('saved');
              }
            }
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Error creating Dashboard', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.snackbar.openSnackBar('Error creating Dashboard', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private isFormValid(): boolean {
    if (!this.dashboard.dashboard_name || !this.dashboard.dashboard_type) {
      return false;
    }
    for (const sheet of this.dashboard.data_sheets || []) {
      if (!sheet.name || !sheet.action_code) return false;
    }
    for (const filter of this.dashboard.filters || []) {
      if (!filter.name || !filter.predicate) return false;
    }
    return true;
  }

  private buildFilterArrayForSave(): any[] {
    const dashFilters: any[] = [];
    const differenceFilters: any[] = [];

    (this.clonedDashboardFilters || []).forEach((filter, index) => {
      (filter.sheet || []).forEach((sheetName) => {
        differenceFilters.push({
          name: filter.name,
          type: filter.type,
          sheet: sheetName,
          predicate: filter.predicate,
          filter_key: filter.filter_key,
          filter_order: index,
          isDeleted: 'N',
        });
      });
    });

    (this.dashboard.filters || []).forEach((filter, index) => {
      (filter.sheet || []).forEach((sheetName) => {
        dashFilters.push({
          name: filter.name,
          type: filter.type,
          sheet: sheetName,
          predicate: filter.predicate,
          filter_key: filter.filter_key,
          filter_order: index,
          isDeleted: 'N',
        });
      });
    });

    const removed = _.differenceWith(differenceFilters, dashFilters, _.isEqual);
    removed.forEach((item: any) => {
      item.isDeleted = 'Y';
    });

    return [...removed, ...dashFilters];
  }

  private buildComponentDataForSave(): any[] {
    const components: any[] = [];
    if (!this.dashboard.components) return components;
    this.dashboard.components.forEach((row, rowIdx) => {
      row.forEach((box, colIdx) => {
        components.push({
          component_key: box.component_id,
          rowindex: rowIdx + 1,
          comp_columns: box.columns,
          comp_order: colIdx,
        });
      });
    });
    return components;
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  /** Allow children to refresh a box that was edited in EditComponentDialogComponent. */
  reloadDashboard(): void {
    if (this.dashboard.dashboard_id) {
      this.loadDashboardByKey(this.dashboard.dashboard_id);
    }
  }
}
